import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { uploadHrLeaveBalances } from "../../api/api";

// ======================================================
// SAMPLE TEMPLATE ROWS PER MODE
// ======================================================

const SAMPLE_ROWS = {
  set: {
    filename: "sample_set_balance.csv",
    headers: ["emp_code", "opening_balance"],
    rows: [
      ["EMP1001", "12.5"],
      ["EMP1002", "8"],
      ["EMP1003", "15.5"],
    ],
  },
  credit: {
    filename: "sample_credit_balance.csv",
    headers: ["emp_code", "amount"],
    rows: [
      ["EMP1001", "1.5"],
      ["EMP1002", "2.0"],
      ["EMP1003", "3.5"],
    ],
  },
  debit: {
    filename: "sample_debit_balance.csv",
    headers: ["emp_code", "amount"],
    rows: [
      ["EMP1001", "1"],
      ["EMP1002", "0.5"],
      ["EMP1003", "2"],
    ],
  },
};

// ======================================================
// CONFIRMATION MESSAGE PER MODE
// ======================================================

const CONFIRM_MESSAGES = {
  set: {
    title: "Set Balance",
    body: "Each employee's balance will be REPLACED with the exact value in the 'opening_balance' column. Their previous balance will be lost — this does not add or subtract, it overwrites.",
  },
  credit: {
    title: "Credit (Add)",
    body: "Each employee's 'amount' will be ADDED to their current balance. Uploading the same file twice will add the amount twice — please confirm this file hasn't already been processed.",
  },
  debit: {
    title: "Debit (Subtract)",
    body: "Each employee's 'amount' will be SUBTRACTED from their current balance. This can result in a negative balance if the amount exceeds what they currently have. Uploading the same file twice will subtract twice.",
  },
};

export default function LeaveImport({ open, onClose, onSuccess }) {

  const [mode, setMode] = useState("set");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [confirmStep, setConfirmStep] = useState(false);

  // =====================================================
  // ACTUAL UPLOAD (RUNS AFTER CONFIRMATION)
  // =====================================================

  const doUpload = async () => {
    setUploading(true);
    setError("");
    setResult(null);
    setConfirmStep(false);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("mode", mode);

    try {
      const res = await uploadHrLeaveBalances(formData);
      setResult(res);

      if (res?.success) {
        onSuccess?.();
      }
    } catch (err) {
      setError(err?.errors?.[0] || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // =====================================================
  // UPLOAD BUTTON -> SHOWS CONFIRMATION FIRST
  // =====================================================

  const handleUploadClick = () => {
    if (!file) return;
    setConfirmStep(true);
  };

  const handleClose = () => {
    setMode("set");
    setFile(null);
    setResult(null);
    setError("");
    setConfirmStep(false);
    onClose();
  };

  // =====================================================
  // DOWNLOAD SAMPLE CSV FOR CURRENT MODE
  // =====================================================

  const handleDownloadSample = () => {
    const template = SAMPLE_ROWS[mode];

    const csvContent = [template.headers, ...template.rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = template.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columnHint =
    mode === "set"
      ? `"emp_code" and "opening_balance" columns. Each row sets the balance to that exact value.`
      : `"emp_code" and "amount" columns. Each row's amount will be ${mode === "credit" ? "added to" : "subtracted from"} the current balance.`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Import Opening Balances

        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>

        {confirmStep ? (
          // =====================================================
          // CONFIRMATION STEP
          // =====================================================
          <Alert
            severity="warning"
            icon={<WarningAmberOutlinedIcon />}
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Confirm: {CONFIRM_MESSAGES[mode].title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {CONFIRM_MESSAGES[mode].body}
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              File: {file?.name}
            </Typography>
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Import Mode
            </Typography>

            <ToggleButtonGroup
              value={mode}
              exclusive
              fullWidth
              size="small"
              onChange={(e, val) => val && setMode(val)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="set">Set Balance</ToggleButton>
              <ToggleButton value="credit" color="success">Credit (Add)</ToggleButton>
              <ToggleButton value="debit" color="error">Debit (Subtract)</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              Upload a file with {columnHint}
            </Typography>

            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={handleDownloadSample}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2 }}
            >
              <FileDownloadOutlinedIcon fontSize="small" />
              Download sample CSV for this mode
            </Link>

            <br />

            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Processed: {result.processed_count} | Skipped: {result.skipped?.length || 0} | Errors: {result.errors?.length || 0}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {confirmStep ? (
          <>
            <Button onClick={() => setConfirmStep(false)} disabled={uploading}>
              Cancel
            </Button>

            <Button
              variant="contained"
              color="warning"
              disabled={uploading}
              onClick={doUpload}
            >
              {uploading ? <CircularProgress size={20} /> : "Yes, Proceed"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>
              Close
            </Button>

            <Button
              variant="contained"
              disabled={!file}
              onClick={handleUploadClick}
            >
              Upload
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}